// src/components/productFormFields/MobileCategoryList.tsx

import React, { useState } from "react";
import { Category } from "../../types";
import { IconButton } from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";

interface Props {
    /** Весь список категорий (со вложенностью) */
    categories: Category[];
    /** Вызывается при клике на название категории */
    onCategoryClick: (cat: Category) => void;
}

/**
 * Мобильный список категорий, где
 * - Подкатегории по умолчанию скрыты
 * - Рядом с категорией с дочерними элементами отображается стрелочка
 *   (ExpandMore / ExpandLess)
 * - При клике на стрелочку раскрываем/скрываем подкатегории
 * - При клике на название — выбираем категорию
 */
const MobileCategoryList: React.FC<Props> = ({ categories, onCategoryClick }) => {
    // Множество «расширенных» (открытых) категорий по id
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

    /** Тогглим (открываем/закрываем) подкатегории для категории catId */
    const toggleExpand = (catId: number) => {
        setExpandedIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(catId)) {
                newSet.delete(catId);
            } else {
                newSet.add(catId);
            }
            return newSet;
        });
    };

    /**
     * Рекурсивно рендерит категорию и, если надо, её подкатегории
     * @param cat - текущая категория
     * @param level - уровень вложенности (для отступов)
     */
    const renderCategory = (cat: Category, level: number = 0) => {
        const hasChildren = cat.children && cat.children.length > 0;
        const isExpanded = expandedIds.has(cat.id);

        return (
            <div key={cat.id} style={{ marginLeft: level * 20, marginTop: 4 }}>
                {/* Верхняя строка категории */}
                <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    {/* Само название категории — клик вызывает onCategoryClick */}
                    <span
                        style={{ cursor: "pointer" }}
                        onClick={() => onCategoryClick(cat)}
                    >
                        {cat.name}
                    </span>
                    {/* Иконка «Развернуть / Свернуть» только если есть подкатегории */}
                    {hasChildren && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation(); // чтобы клик не засчитывался за выбор категории
                                toggleExpand(cat.id);
                            }}
                        >
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    )}
                </div>

                {/* Если у категории есть дети и она раскрыта — рендерим их */}
                {hasChildren && isExpanded && (
                    <div>
                        {cat.children!.map((child) => renderCategory(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ maxHeight: 700, overflowY: "auto", width: "100%" }}>
            {categories.map((cat) => renderCategory(cat))}
        </div>
    );
};

export default MobileCategoryList;
